import React from "react";
import { Link } from "react-router-dom";
import TeamDisplay from "./TeamDisplay";
import { formatGameDate, formatGameTime } from "../utils/dateUtils";
import { Game } from "../types/models";

interface GameListItemProps {
  game: Game;
  /**
   * The league abbreviation for constructing links.
   * If not provided, relative links will be used.
   */
  leagueAbbr?: string;
  /**
   * Where the item links to, overriding the public game page. The admin side
   * points its lists at the game's scoreboard instead.
   */
  to?: string;
}

/**
 * Reusable game list item component.
 * The entire item is clickable and links to the game details page.
 * Displays game information with a responsive two-unit layout:
 * - Unit 1: Date + Time/Status (left-aligned)
 * - Unit 2: Teams + Scores (left-aligned)
 * On larger screens, both units appear on one row with space between.
 * On mobile, units wrap as needed.
 */
export default function GameListItem({
  game,
  leagueAbbr,
  to,
}: GameListItemProps) {
  const { day, month } = formatGameDate(game.date);
  const isCompleted = game.state === "completed";
  const isLive = game.state === "playing";
  // A game in progress has a running score worth showing; one not yet started
  // has nothing to report.
  const showScores = isCompleted || isLive;
  const homeWon =
    isCompleted && (game.home_score ?? 0) > (game.visitor_score ?? 0);
  const visitorWon =
    isCompleted && (game.visitor_score ?? 0) > (game.home_score ?? 0);

  // Construct game URL
  const gameUrl =
    to ??
    (leagueAbbr
      ? `/leagues/${leagueAbbr}/games/${game.id}`
      : `games/${game.id}`);

  return (
    <Link
      to={gameUrl}
      className="game-list-item"
      style={{ textDecoration: "none", color: "inherit", display: "flex" }}
    >
      {/* Unit 1: Date + Time/Status */}
      <div className="game-list-datetime">
        <div className="game-list-date">
          <span className="game-list-day">{day}</span>
          <span className="game-list-month">{month}</span>
        </div>
        <div className="game-list-status-group">
          <div className="game-list-time">{formatGameTime(game.date)}</div>
          {isLive ? (
            <span
              className="lp-badge"
              style={{
                backgroundColor: "var(--brand-accent)",
                color: "var(--bg-primary)",
              }}
            >
              Live
            </span>
          ) : isCompleted ? (
            <span className="lp-badge lp-badge-final">Final</span>
          ) : null}
        </div>
      </div>

      {/* Unit 2: Teams + Scores */}
      <div className="game-list-matchup">
        {/* Visitor Team Row */}
        <div className="game-list-team-row">
          <div className={`game-list-team-name${visitorWon ? " winner" : ""}`}>
            <TeamDisplay
              team={{
                id: game.visitor_team_id,
                name: game.visitor_team_name ?? "Visitor Team",
                colour_code: game.visitor_team_colour_code,
              }}
            />
          </div>
          <span className={`game-list-score${visitorWon ? " winner" : ""}`}>
            {showScores ? game.visitor_score ?? 0 : "-"}
          </span>
        </div>

        {/* Home Team Row */}
        <div className="game-list-team-row">
          <div className={`game-list-team-name${homeWon ? " winner" : ""}`}>
            <TeamDisplay
              team={{
                id: game.home_team_id,
                name: game.home_team_name ?? "Home Team",
                colour_code: game.home_team_colour_code,
              }}
            />
          </div>
          <span className={`game-list-score${homeWon ? " winner" : ""}`}>
            {showScores ? game.home_score ?? 0 : "-"}
          </span>
        </div>
      </div>
    </Link>
  );
}
